import React, { useState, useEffect } from "react";
import {
	CraftModal,
	CraftModalHeader,
	CraftModalBody,
	CraftModalFooter,
	CraftButton,
	CraftInputText,
	CraftTextarea,
	CraftSpinner,
	CraftDivider,
	CraftTag,
} from "@craft-design-system/core-components-react";
import { Send, FileText } from "lucide-react";
import { apiClient } from "../../services/api";
import { getFrontendBaseUrl } from "../../utils/apiConfig";

const ReminderEmailModal = ({
	open,
	onClose,
	selectedRecords = [],
	fromEmail = "",
	getUserDisplayName,
	viewRoute = "/vff",
}) => {
	const [formData, setFormData] = useState({
		toEmails: "",
		ccEmails: "",
		subject: "",
		message: "",
	});
	const [loading, setLoading] = useState(false);
	const [success, setSuccess] = useState("");
	const [error, setError] = useState("");
	const [emailList, setEmailList] = useState([]);
	const [ccEmailList, setCcEmailList] = useState([]); // Reset form when modal opens/closes
	useEffect(() => {
		if (open) {
			// Pre-populate CC with the sender's own email so they always get a copy
			const senderEmail = fromEmail || "";
			setFormData({
				toEmails: "",
				ccEmails: senderEmail,
				subject: `Parts Tracking Reminder - ${selectedRecords.length} records require attention`,
				message: `Hi,\n\nI hope this message finds you well. I wanted to bring to your attention ${selectedRecords.length} records in our parts tracking system that may require your review or action.\n\nPlease find the details of these records below. If you have any questions or need additional information, please don't hesitate to reach out.\n\nBest regards,\n${getUserDisplayName ? getUserDisplayName() : "Parts Tracking User"}`,
			});
			setEmailList([]);
			// Pre-populate CC chip list with sender's email
			setCcEmailList(senderEmail ? [senderEmail] : []);
			setSuccess("");
			setError("");
			setLoading(false);
		}
	}, [open, selectedRecords.length, getUserDisplayName, fromEmail]);
	const handleInputChange = (field) => (event) => {
		setFormData((prev) => ({
			...prev,
			[field]: event.target.value,
		}));

		// Parse emails for the To field
		if (field === "toEmails") {
			const emails = event.target.value
				.split(/[,;\s]+/)
				.map((email) => email.trim())
				.filter((email) => email && email.includes("@"));
			setEmailList(emails);
		}

		// Parse emails for the CC field
		if (field === "ccEmails") {
			const emails = event.target.value
				.split(/[,;\s]+/)
				.map((email) => email.trim())
				.filter((email) => email && email.includes("@"));
			setCcEmailList(emails);
		}
	};
	const generateViewUrl = () => {
		if (selectedRecords.length === 0) return null;
		// Get the correct frontend base URL for the current environment
		const baseUrl = getFrontendBaseUrl();

		// Create kast_key parameter for URL
		const kastKeys = selectedRecords.map((record) => record.kast_key).join(",");

		// Generate URL using the route provided by the caller (e.g. /vff or /series-tracking)
		const viewUrl = `${baseUrl}${viewRoute}?kast_keys=${encodeURIComponent(kastKeys)}`;

		console.log("Generated view URL for email:", viewUrl);
		console.log("Kast keys:", kastKeys);
		console.log("Frontend base URL:", baseUrl);
		console.log("View route:", viewRoute);

		return viewUrl;
	};
	const handleSendEmail = async () => {
		if (!formData.toEmails.trim()) {
			setError("Please enter at least one email address");
			return;
		}

		if (!formData.subject.trim()) {
			setError("Please enter a subject");
			return;
		}

		if (!formData.message.trim()) {
			setError("Please enter a message");
			return;
		}

		if (emailList.length === 0) {
			setError("Please enter valid email addresses");
			return;
		}
		setLoading(true);
		setError("");

		try {
			// Always include the sender in CC so they receive a copy of what was sent.
			// Merge any CC addresses the user typed with the sender's address,
			// deduplicating so the sender only appears once.
			const ccWithSender = fromEmail
				? Array.from(new Set([fromEmail, ...ccEmailList]))
				: ccEmailList;

			const emailData = {
				from_email: fromEmail,
				to_emails: emailList,
				cc_emails: ccWithSender,
				subject: formData.subject,
				message: formData.message,
				selected_records: selectedRecords.map((record) => ({
					kast_key: record.kast_key,
					vehicle_no: record.vehicle_no,
					part_no: record.part_no,
					part_description: record.part_description,
					supplier: record.supplier,
					required_date: record.required_date,
				})),
				view_url: generateViewUrl(),
			};
			const response = await apiClient.post(
				"/api/send-reminder-email",
				emailData,
			);
			// Count only external recipients — To list + CC addresses that are not the sender
			const externalCcCount = ccWithSender.filter(
				(email) => email !== fromEmail,
			).length;
			const totalRecipients = emailList.length + externalCcCount;
			setSuccess(`Email sent successfully to ${totalRecipients} recipient(s)!`);
			setTimeout(() => {
				onClose();
			}, 2000);
		} catch (err) {
			console.error("Email send error:", err);
			if (err.response?.data?.detail) {
				setError(err.response.data.detail);
			} else if (err.response?.status === 401) {
				setError("Authentication failed. Please log in again.");
			} else if (err.response?.status >= 500) {
				setError("Server error. Please try again later.");
			} else {
				setError(
					"Failed to send email. Please check your internet connection and try again.",
				);
			}
		} finally {
			setLoading(false);
		}
	};
	const removeEmail = (emailToRemove) => {
		const updatedEmails = emailList.filter((email) => email !== emailToRemove);
		setEmailList(updatedEmails);
		setFormData((prev) => ({
			...prev,
			toEmails: updatedEmails.join(", "),
		}));
	};

	const removeCcEmail = (emailToRemove) => {
		const updatedEmails = ccEmailList.filter(
			(email) => email !== emailToRemove,
		);
		setCcEmailList(updatedEmails);
		setFormData((prev) => ({
			...prev,
			ccEmails: updatedEmails.join(", "),
		}));
	};
	const s = {
		label: {
			display: "block",
			fontSize: "13px",
			fontFamily: "MAN EUROPE_Bold, Arial, sans-serif",
			color: "#374151",
			marginBottom: "6px",
		},
		required: { color: "#dc3545", marginLeft: "2px" },
		chipRow: {
			display: "flex",
			flexWrap: "wrap",
			gap: "6px",
			marginTop: "6px",
		},
		chip: {
			display: "inline-flex",
			alignItems: "center",
			gap: "4px",
			backgroundColor: "#f0f8ff",
			border: "1px solid #b3d9ff",
			borderRadius: "12px",
			padding: "2px 10px 2px 10px",
			fontSize: "12px",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		chipCC: {
			display: "inline-flex",
			alignItems: "center",
			gap: "4px",
			backgroundColor: "#fff4e6",
			border: "1px solid #ffd699",
			borderRadius: "12px",
			padding: "2px 10px 2px 10px",
			fontSize: "12px",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		chipClose: {
			background: "none",
			border: "none",
			cursor: "pointer",
			padding: "0 0 0 4px",
			color: "#666",
			fontSize: "14px",
			lineHeight: 1,
		},
		alert: (type) => ({
			padding: "10px 14px",
			borderRadius: "6px",
			marginBottom: "16px",
			fontSize: "13px",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
			backgroundColor: type === "success" ? "#e8f5e9" : "#fde8e8",
			border: `1px solid ${type === "success" ? "#a5d6a7" : "#f5c6c6"}`,
			color: type === "success" ? "#2e7d32" : "#cc0000",
		}),
		fieldWrap: { marginBottom: "16px" },
		recordsBox: {
			maxHeight: "180px",
			overflowY: "auto",
			border: "1px solid #e5e7eb",
			borderRadius: "6px",
			backgroundColor: "#f8f9fa",
		},
		recordRow: {
			padding: "8px 12px",
			borderBottom: "1px solid #e5e7eb",
			display: "flex",
			gap: "12px",
			flexWrap: "wrap",
			fontSize: "13px",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		infoBox: {
			backgroundColor: "#f0f8ff",
			border: "1px solid #b3d9ff",
			borderRadius: "6px",
			padding: "10px 14px",
			fontSize: "12px",
			color: "#1a5276",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
			lineHeight: 1.5,
		},
	};

	return (
		<CraftModal show={open} allowClickOut={false} onCraftClose={onClose}>
			<CraftModalHeader
				header="Send Reminder Email"
				content={`Sending reminder for ${selectedRecords.length} selected record${selectedRecords.length !== 1 ? "s" : ""}.`}
			/>

			<CraftModalBody>
				{/* Alerts */}
				{success && <div style={s.alert("success")}>✅ {success}</div>}
				{error && <div style={s.alert("error")}>⚠️ {error}</div>}

				{/* From (read-only) */}
				<div style={s.fieldWrap}>
					<CraftInputText
						label="From"
						value={fromEmail}
						disabled
						style={{ width: "100%" }}
					/>
				</div>

				{/* To */}
				<div style={s.fieldWrap}>
					<CraftInputText
						type="email"
						label="To"
						required
						value={formData.toEmails}
						onInput={handleInputChange("toEmails")}
						placeholder="Enter email addresses separated by commas or spaces"
						disabled={loading}
						style={{ width: "100%" }}
					/>
					{emailList.length > 0 && (
						<div style={s.chipRow}>
							{emailList.map((email, i) => (
								<span key={i} style={s.chip}>
									{email}
									<button
										style={s.chipClose}
										onClick={() => removeEmail(email)}
									>
										×
									</button>
								</span>
							))}
						</div>
					)}
				</div>

				{/* CC */}
				<div style={s.fieldWrap}>
					<CraftInputText
						type="email"
						label="CC"
						value={formData.ccEmails}
						onInput={handleInputChange("ccEmails")}
						placeholder="Enter CC email addresses"
						disabled={loading}
						style={{ width: "100%" }}
					/>
					{ccEmailList.length > 0 && (
						<div style={s.chipRow}>
							{ccEmailList.map((email, i) => (
								<span key={i} style={s.chipCC}>
									{email}
									<button
										style={s.chipClose}
										onClick={() => removeCcEmail(email)}
									>
										×
									</button>
								</span>
							))}
						</div>
					)}
				</div>

				{/* Subject */}
				<div style={s.fieldWrap}>
					<CraftInputText
						label="Subject"
						required
						value={formData.subject}
						onInput={handleInputChange("subject")}
						placeholder="Enter email subject"
						disabled={loading}
						style={{ width: "100%" }}
					/>
				</div>

				{/* Message */}
				<div style={s.fieldWrap}>
					<CraftTextarea
						label="Message"
						required
						value={formData.message}
						onInput={handleInputChange("message")}
						placeholder="Enter your message..."
						disabled={loading}
						rows={6}
						style={{ width: "100%" }}
					/>
				</div>

				<CraftDivider />

				{/* Selected Records */}
				<div style={{ margin: "16px 0 12px" }}>
					<div
						style={{
							...s.label,
							display: "flex",
							alignItems: "center",
							gap: "6px",
							marginBottom: "10px",
						}}
					>
						<FileText size={15} />
						Selected Records ({selectedRecords.length} items)
					</div>
					<div style={s.recordsBox}>
						{selectedRecords.slice(0, 10).map((record, i) => (
							<div
								key={record.kast_key || i}
								style={{
									...s.recordRow,
									borderBottom:
										i < Math.min(selectedRecords.length, 10) - 1
											? "1px solid #e5e7eb"
											: "none",
								}}
							>
								<span
									style={{
										fontFamily: "MAN EUROPE_Bold, Arial, sans-serif",
										minWidth: "80px",
									}}
								>
									{record.vehicle_no || "N/A"}
								</span>
								<span style={{ minWidth: "120px" }}>
									{record.part_no || "N/A"}
								</span>
								<span style={{ color: "#666", flex: 1 }}>
									{record.part_description || "N/A"}
								</span>
							</div>
						))}
						{selectedRecords.length > 10 && (
							<div
								style={{
									...s.recordRow,
									justifyContent: "center",
									color: "#666",
									fontStyle: "italic",
									backgroundColor: "#f0f0f0",
									borderBottom: "none",
								}}
							>
								... and {selectedRecords.length - 10} more records
							</div>
						)}
					</div>
				</div>

				{/* Direct link info */}
				<div style={s.infoBox}>
					<strong>📎 Direct Link Included</strong>
					<br />
					Recipients will receive a direct link to view these specific records
					in the Parts Tracking tool.
				</div>
			</CraftModalBody>

			<CraftModalFooter>
				<CraftButton
					variant="plain"
					size="m"
					onClick={onClose}
					disabled={loading}
				>
					Cancel
				</CraftButton>
				<CraftButton
					variant="primary"
					size="m"
					onClick={handleSendEmail}
					disabled={
						loading ||
						!formData.toEmails.trim() ||
						!formData.subject.trim() ||
						!formData.message.trim()
					}
				>
					{loading ? (
						<span style={{ display: "flex", alignItems: "center", gap: "7px" }}>
							<CraftSpinner size="s" /> Sending…
						</span>
					) : (
						<span style={{ display: "flex", alignItems: "center", gap: "7px" }}>
							<Send size={15} /> Send Email
						</span>
					)}
				</CraftButton>
			</CraftModalFooter>
		</CraftModal>
	);
};

export default ReminderEmailModal;
